import React from 'react';
import {
  Row,
  Button,
  Tooltip,
} from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import TitleRow from './TitleRow';

import PhotoReel from '../../../common/photos/PhotoReel';

import FieldTriggerFlag from './FieldTriggerFlag';
import FieldTriggerCheckbox from './FieldTriggerCheckbox';
import ConditionalRenderingRow from './ConditionalRenderingRow';
import OptionalRow from './OptionalRow';
import HideIfEmptyInPDFCheckbox from './HideIfEmptyInPDFCheckbox';
import OnTraccrTextInput from '../../../common/inputs/OnTraccrTextInput';
import { uuid } from '../../../helpers/helpers';
import OnTraccrCheckbox from '../../../common/inputs/OnTraccrCheckbox';
import FileTileView from '../../../common/files/FileTileView/FileTileView';

const configure = ({
  id,
  setConfigProps,
  configProps = {},
  setCanSubmit,
  sections = [],
  isExternalForm,
  templateId,
  divisions,
  projectId,
  name,
  isBoardCards,
  setFieldTriggerEditable,
  disableOptional,
  customers = [],
  projects = [],
  users = [],
  costcodes = [],
  phases = [],
  projectIdMap = {},
  vendors = [],
  equipment = [],
  formTemplates = [],
  contactAddressBooks = {},
  labels = [],
  buckets = [],
  scheduleCategories = [],
  isV3Form = false,
} = {}) => {
  const {
    optional,
    hasConditionalRendering = false,
    conditionalRenderingFormula,
    append,
  } = configProps;

  const setTitle = (e) => {
    const {
      target: {
        value,
      } = {},
    } = e;
    setConfigProps({
      ...configProps,
      title: value,
    });
    setCanSubmit(value);
  };

  const setOptional = (checked) => {
    setConfigProps({
      ...configProps,
      optional: checked,
    });
  };

  const setConditionalRenderingFormula = (newVal) => {
    setConfigProps({
      ...configProps,
      conditionalRenderingFormula: newVal,
    });
  };

  const setHasConditionalRendering = (checked) => {
    setConfigProps({
      ...configProps,
      hasConditionalRendering: checked,
    });
  };

  const setFieldTrigger = (checked) => {
    setConfigProps({
      ...configProps,
      fieldTrigger: checked,
    });
  };

  const onAppendChange = (checked) => {
    setConfigProps({
      ...configProps,
      append: checked,
    });
  };

  return (
    <div>
      <Row className="form-required-field">
        Title:
      </Row>
      <Row style={{ marginTop: 5 }}>
        <OnTraccrTextInput
          textarea
          placeholder="Insert title here"
          onChange={setTitle}
          value={configProps.title}
        />
      </Row>
      <OptionalRow onChange={setOptional} optional={optional} disabled={disableOptional} />
      <ConditionalRenderingRow
        id={id}
        sections={sections}
        customers={customers}
        projects={projects}
        users={users}
        costcodes={costcodes}
        phases={phases}
        projectIdMap={projectIdMap}
        vendors={vendors}
        equipment={equipment}
        formTemplates={formTemplates}
        labels={labels}
        contactAddressBooks={contactAddressBooks}
        buckets={buckets}
        scheduleCategories={scheduleCategories}
        onChange={setHasConditionalRendering}
        onFormulaChange={setConditionalRenderingFormula}
        hasConditionalRendering={hasConditionalRendering}
        conditionalRenderingFormula={conditionalRenderingFormula}
      />
      <HideIfEmptyInPDFCheckbox
        isV3Form={isV3Form}
        configProps={configProps}
        setConfigProps={setConfigProps}
      />
      <OnTraccrCheckbox
        style={{ marginTop: 10 }}
        label="Append to Form PDF"
        value={append}
        onChange={onAppendChange}
        hoverNode="If checked, any PDF files and images will be appended to the generated form PDF"
      />
      {!isBoardCards && !isExternalForm && (
        <FieldTriggerCheckbox
          onChange={setFieldTrigger}
          onEditableChange={setFieldTriggerEditable}
          sections={sections}
          projectId={projectId}
          templateId={templateId}
          divisions={divisions}
          configProps={configProps}
          setConfigProps={setConfigProps}
          name={name}
        />
      )}
    </div>
  );
};

const preview = ({
  setPreviewProps,
  previewProps = {},
  configProps = {},
  id,
  onOpenFileModal,
  onOpenFile,
  setCurrFileFieldId,
  setOpenFileIndex,
  responses = {},
  setResponses,
  responding = false,
  isDisplay,
  sections,
  projectId,
  templateId,
  divisions,
  showCondensedView,
  fileMap,
  editing = false,
  setCurrFieldId,
  onFieldTrigger,
  isFormBuilder,
  isExternalForm = false,
} = {}) => {
  const externalTemplateId = isExternalForm ? templateId : null;
  const isFieldDisplay = !isFormBuilder && isDisplay;
  const {
    optional,
    title = 'Title goes here',
    showButton = true,
    fieldTrigger,
  } = configProps;

  const {
    files = [],
    onClick,
    hideDelete,
    timestamps = {},
    pendingAttachmentEntries = [],
  } = previewProps;

  const {
    [id]: {
      files: responseFiles = [],
      timestamps: responseTimestamps = {},
    } = {},
  } = responses;

  const onAttachDelete = hideDelete ? null : (idx) => {
    if (responding) {
      // set responses without the file in question
      const newFiles = [...responseFiles];
      newFiles.splice(idx, 1);
      setResponses({
        ...responses,
        [id]: {
          ...(responses[id]),
          files: newFiles,
        },
      });
    } else {
      const newFiles = [...files];
      newFiles.splice(idx, 1);
      setPreviewProps({
        ...previewProps,
        files: newFiles,
      });
    }
  };
  const onAttachAdd = () => {
    if (responding) {
      onOpenFileModal(id);
    } else {
      const newFiles = files.concat([new File([], `file-${files.length + 1}.png`, { autoSaveId: uuid() })]);
      setPreviewProps({
        ...previewProps,
        files: newFiles,
      });
    }
  };

  const onFileClick = (index) => {
    if (responding) {
      const file = responseFiles[index];
      if (setOpenFileIndex) setOpenFileIndex(index);
      if (setCurrFileFieldId) setCurrFileFieldId(id);
      onOpenFile(file);
      setCurrFieldId(id);
    } else {
      onClick(index);
    }
  };

  const pendingAttachmentNodes = isFieldDisplay
    ? pendingAttachmentEntries.flatMap((entry) => (
      (entry.files ?? []).map((file, fileIndex) => (
        <div
          key={`${entry.formId}-${file.id}`}
          className="pending-attachment-row"
        >
          <Tooltip
            title={`This file is pending from a form awaiting approval: ${entry.pendingTableFormName}`}
            placement="top"
          >
            <InfoCircleOutlined className="pending-text-value-icon" />
          </Tooltip>
          <div className="pending-attachment-tile">
            <FileTileView
              files={[file]}
              onClick={() => entry.onClick(fileIndex)}
              canDownload={isFieldDisplay}
              externalTemplateId={externalTemplateId}
            />
          </div>
        </div>
      ))
    ))
    : null;

  return (
    <div>
      <TitleRow
        title={title}
        optional={optional}
        filter={
          fieldTrigger && !isDisplay
            ? (
              <FieldTriggerFlag
                sections={sections}
                templateId={templateId}
                projectId={projectId}
                divisions={divisions}
                configProps={configProps}
                responding={responding}
                onFieldTrigger={onFieldTrigger}
              />
            ) : null
        }
        isFieldDisplay={isFieldDisplay}
      />
      {showButton && !isDisplay && (
        <Row style={{ marginTop: 10 }}>
          <Button
            className="form-attachment-add-button-redesign"
            onClick={onAttachAdd}
          >
            <div className="form-attachment-add-button-center">
              <div className="file-tile-download-icon">
                <svg width="10" height="15" viewBox="0 0 10 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path fillRule="evenodd" clipRule="evenodd" d="M1.33333 4.66728L1.33333 10.6667C1.33333 11.0349 1.03486 11.3333 0.666667 11.3333C0.298477 11.3333 -5.54164e-10 10.9931 0 10.6249C0 10.6249 0.000193711 4.67349 0.000212247 4.66728C0.000208772 4.64468 0.000593776 4.62212 0.00134655 4.59954C0.00258517 4.56238 0.00495923 4.51096 0.0095065 4.4473C0.0185848 4.3202 0.0364185 4.14298 0.0715701 3.93207C0.141347 3.51341 0.282142 2.945 0.57038 2.36852C0.859924 1.78943 1.30471 1.19007 1.98437 0.736969C2.6682 0.281082 3.55001 0 4.66667 0C5.79688 0 6.66514 0.281082 7.34897 0.736969C8.02863 1.19007 8.47341 1.78943 8.76295 2.36852C9.05119 2.945 9.19199 3.51341 9.26176 3.93207C9.29691 4.14298 9.31475 4.3202 9.32383 4.4473C9.32837 4.51096 9.33075 4.56238 9.33199 4.59954C9.33273 4.62191 9.33312 4.64428 9.33333 4.66666C9.34296 6.5126 9.33351 11.1454 9.33333 11.334C9.33332 11.3512 9.33288 11.3683 9.33231 11.3855C9.3314 11.4129 9.32968 11.45 9.32643 11.4954C9.31996 11.5861 9.30733 11.7112 9.2826 11.8596C9.23365 12.1533 9.13452 12.555 8.92962 12.9648C8.72341 13.3772 8.40362 13.8099 7.91147 14.138C7.41514 14.4689 6.78332 14.6667 6 14.6667C5.21668 14.6667 4.58486 14.4689 4.08853 14.138C3.59638 13.8099 3.27659 13.3772 3.07038 12.9648C2.86548 12.555 2.76635 12.1533 2.7174 11.8596C2.69267 11.7112 2.68004 11.5861 2.67357 11.4954C2.67032 11.45 2.6686 11.4129 2.66769 11.3855C2.66712 11.3686 2.66672 11.3517 2.66667 11.3348C2.66414 9.3363 2.66667 7.33781 2.66667 5.33935C2.66734 5.30482 2.6685 5.263 2.67097 5.22855C2.67484 5.17436 2.68225 5.10131 2.69657 5.0154C2.72468 4.84674 2.78215 4.61166 2.90372 4.36852C3.02659 4.12277 3.22138 3.85674 3.52603 3.65364C3.83486 3.44775 4.21668 3.33333 4.66667 3.33333C5.11666 3.33333 5.49847 3.44775 5.8073 3.65364C6.11195 3.85674 6.30674 4.12277 6.42962 4.36852C6.55119 4.61166 6.60865 4.84674 6.63676 5.0154C6.65108 5.10131 6.6585 5.17436 6.66237 5.22855C6.66563 5.27416 6.66658 5.30238 6.66666 5.33196L6.66667 10.6667C6.66667 11.0349 6.36819 11.3333 6 11.3333C5.63181 11.3333 5.33333 11.0349 5.33333 10.6667V5.33935C5.33298 5.33029 5.33079 5.28989 5.32157 5.2346C5.30801 5.15326 5.28214 5.055 5.23705 4.96481C5.19326 4.87723 5.13804 4.80993 5.0677 4.76303C5.00153 4.71892 4.88334 4.66667 4.66667 4.66667C4.44999 4.66667 4.33181 4.71892 4.26564 4.76303C4.19529 4.80993 4.14008 4.87723 4.09629 4.96481C4.05119 5.055 4.02532 5.15326 4.01176 5.2346C4.00255 5.28989 4.00036 5.33029 4 5.33935V11.3304C4.00062 11.3538 4.00185 11.3771 4.00351 11.4004C4.00746 11.4556 4.01566 11.5388 4.0326 11.6404C4.06699 11.8467 4.13452 12.1117 4.26295 12.3685C4.39008 12.6228 4.57029 12.8567 4.82813 13.0286C5.0818 13.1977 5.44999 13.3333 6 13.3333C6.55001 13.3333 6.9182 13.1977 7.17187 13.0286C7.42971 12.8567 7.60992 12.6228 7.73705 12.3685C7.86548 12.1117 7.93301 11.8467 7.9674 11.6404C7.98434 11.5388 7.99254 11.4556 7.99649 11.4004C7.99815 11.3771 7.99938 11.3538 8 11.3304L7.99996 4.66594C7.99978 4.65432 7.99878 4.61083 7.99388 4.54229C7.98733 4.45064 7.97392 4.31536 7.94657 4.15127C7.89135 3.81993 7.78214 3.38834 7.57038 2.96481C7.35993 2.5439 7.05471 2.14326 6.60937 1.84636C6.1682 1.55225 5.55001 1.33333 4.66667 1.33333C3.78332 1.33333 3.16514 1.55225 2.72397 1.84636C2.27862 2.14326 1.97341 2.5439 1.76295 2.96481C1.55119 3.38834 1.44199 3.81993 1.38676 4.15127C1.35941 4.31536 1.346 4.45064 1.33945 4.54229C1.335 4.60458 1.33411 4.63806 1.33333 4.66728Z" fill="#7396B6" />
                </svg>
              </div>
              <span>
                Click to add attachment
              </span>
            </div>
          </Button>
        </Row>
      )}
      <div style={{ pointerEvents: 'all' }}>
        {showCondensedView
          ? (
            <PhotoReel
              fileMap={fileMap}
              editing={editing}
              small
              files={responding ? responseFiles : files}
              timestamps={responding ? responseTimestamps : timestamps}
              onDelete={isDisplay ? null : onAttachDelete}
              onClick={onFileClick}
              showCondensedView={showCondensedView}
            />
          ) : (
            <FileTileView
              files={responding ? responseFiles : files}
              onClick={onFileClick}
              canDownload={isFieldDisplay}
              onDelete={isDisplay ? null : onAttachDelete}
              externalTemplateId={externalTemplateId}
            />
          )}
        {pendingAttachmentNodes}
      </div>
    </div>
  );
};

export default {
  configure,
  preview,
  title: 'Attachment',
  description: 'User can add one or more files/photos',
};
